import { Fingerprint, Languages, AlertTriangle } from 'lucide-react';

const METHOD_LABELS = {
  exact: 'Exact name',
  normalised_exact: 'Exact after normalisation',
  reordered_name: 'Same words, different order',
  partial_name: 'Partial name',
  acronym: 'Acronym / short form',
  initials: 'Initials',
  phonetic: 'Phonetic',
  fuzzy: 'Fuzzy',
};

const FORM_KIND_LABELS = {
  original: 'name as entered',
  reordered: 'reordered name',
  transliteration: 'transliteration',
  transliteration_variant: 'transliteration variant',
  kb_variant: 'variant knowledge base',
};

const FACTOR_LABELS = {
  dob: 'Date of birth',
  nationality: 'Nationality',
  passport: 'Passport',
  national_id: 'National ID',
  place_of_birth: 'Place of birth',
  address: 'Address',
  gender: 'Gender',
};

const matchTone = (type) => {
  if (type === 'exact' || type === 'exact_other_document' || type === 'match') {
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }
  if (type === 'partial' || type === 'year_only') {
    return 'bg-amber-50 border-amber-200 text-amber-700';
  }
  if (type === 'no_match' || type === 'mismatch' || type === 'weak') {
    return 'bg-rose-50 border-rose-200 text-rose-700';
  }
  return 'bg-slate-50 border-slate-200 text-slate-500';
};

/** How a hit was produced: method, which representation of the query, identifier scores. */
export const MatchEvidence = ({ match, index }) => {
  const details = match?.details || {};
  const iv = match?.identity_verification || {};
  const factors = iv.match_factors || {};
  const contradictions = iv.contradictions || [];

  const scored = Object.entries(factors).filter(
    ([key, factor]) =>
      key !== 'name' && factor && !['not_provided'].includes(factor.match_type || factor.result)
  );

  if (!details.match_method && scored.length === 0) return null;

  return (
    <div
      className="mt-3 p-3 bg-white border border-slate-200 rounded space-y-2"
      data-testid={`match-evidence-${index}`}
    >
      {details.match_method && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
          <Fingerprint className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-semibold text-slate-800">
            {METHOD_LABELS[details.match_method] || details.match_method}
          </span>
          <span className="text-slate-400">·</span>
          <span>
            matched on {FORM_KIND_LABELS[details.matched_form_kind] || details.matched_form_kind}
          </span>
          {details.matched_form_kind !== 'original' && details.matched_via_form && (
            <>
              <span className="text-slate-400">·</span>
              <span className="font-mono text-[11px] text-slate-700">{details.matched_via_form}</span>
            </>
          )}
          {typeof details.raw_name_score === 'number' && (
            <>
              <span className="text-slate-400">·</span>
              <span>name {details.raw_name_score}%</span>
            </>
          )}
          {typeof iv.combined_score === 'number' && (
            <>
              <span className="text-slate-400">·</span>
              <span>confidence {iv.combined_score}%</span>
            </>
          )}
        </div>
      )}

      {details.match_provenance && details.matched_form_kind !== 'original' && (
        <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Languages className="w-3.5 h-3.5 text-slate-400" />
          {details.match_provenance}
          {details.matched_variant_type ? ` · ${details.matched_variant_type.replace(/_/g, ' ')}` : ''}
        </p>
      )}

      {scored.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`identifier-factors-${index}`}>
          {scored.map(([key, factor]) => (
            <span
              key={`factor-${key}-${index}`}
              className={`px-2 py-0.5 rounded border text-[11px] ${matchTone(factor.match_type || factor.result)}`}
            >
              {FACTOR_LABELS[key] || key}:{' '}
              {(factor.match_type || factor.result || '').replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      {contradictions.length > 0 && (
        <p
          className="flex items-start gap-1.5 text-[11px] text-rose-700"
          data-testid={`contradictions-${index}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Contradicts on {contradictions.map((c) => FACTOR_LABELS[c.field] || c.field).join(', ')}
            {typeof iv.contradiction_penalty === 'number'
              ? ` — confidence reduced by ${Math.round(iv.contradiction_penalty * 100)}%`
              : ''}
          </span>
        </p>
      )}
    </div>
  );
};

/** Reproducibility record: script detected, transliterations used, rule-set versions. */
export const ScreeningTracePanel = ({ trace }) => {
  if (!trace) return null;
  const rows = [
    ['Input as entered', trace.original_input],
    ['Detected script', trace.detected_script],
    ['Transliteration', trace.transliteration_primary],
    ['Transliteration variants', (trace.transliteration_variants || []).join(', ')],
    ['Rule set', trace.transliteration_primary
      ? `${trace.transliteration_ruleset} v${trace.transliteration_ruleset_version} (${trace.transliteration_engine})`
      : null],
    ['Variant knowledge base', `${trace.variant_kb} v${trace.variant_kb_version}`],
  ].filter(([, value]) => value);

  return (
    <div
      className="p-4 bg-slate-50 border border-slate-200 rounded"
      data-testid="screening-trace-panel"
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
        Screening trace — reproducible record
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
        {rows.map(([label, value]) => (
          <div key={`trace-${label}`}>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
            <p className="text-xs text-slate-800 break-words">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
